import React, { useContext, useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { MarketplaceContext } from 'src/contexts/marketplace';
import LinkInBioHeader from '../link-in-bio-header';
import * as Sentry from '@sentry/react';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  fetchClientSecret: () => Promise<string>;
}

const CheckoutModal = ({ isOpen, onClose, fetchClientSecret }: CheckoutModalProps) => {
  const { marketplace, stripePromise } = useContext(MarketplaceContext);
  const [checkoutLoaded, setCheckoutLoaded] = useState(false);

  // Send an error to Sentry if the checkout hasn't loaded within 10s
  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      if (!checkoutLoaded) {
        Sentry.captureMessage('Stripe checkout took longer than 10 seconds to load.', {
          level: 'error',
          tags: {
            component: 'CheckoutModal',
            marketplaceId: marketplace?.id,
          },
        });
      }
    }, 10000);

    return () => clearTimeout(timer);
  }, [isOpen, checkoutLoaded, marketplace?.id]);

  // Wrap fetchClientSecret so we can mark the checkout as loaded
  const wrappedFetchClientSecret = async () => {
    const clientSecret = await fetchClientSecret();
    // Once we have the client secret, we assume the checkout can mount
    setCheckoutLoaded(true);
    return clientSecret;
  };

  // Error boundary wrapper for the Stripe checkout
  const handleError = (error: unknown) => {
    Sentry.captureException(error, {
      tags: {
        component: 'CheckoutModal',
        marketplaceId: marketplace?.id,
      },
      extra: {
        hasStripePromise: !!stripePromise,
        error,
      },
    });
  };

  useEffect(() => {
    if (isOpen && !stripePromise) {
      Sentry.captureMessage('Stripe Promise missing in CheckoutModal', {
        level: 'error',
        tags: {
          component: 'CheckoutModal',
          marketplaceId: marketplace?.id,
        },
      });
    }
  }, [isOpen, stripePromise, marketplace?.id]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent maxWidth={{ base: '500px', lg: '1040px' }} maxHeight="85vh">
        <ModalHeader />
        <ModalCloseButton />
        <ModalBody overflowY="auto">
          <LinkInBioHeader marketplace={marketplace} />
          {stripePromise !== null && (
            <Sentry.ErrorBoundary
              fallback={
                <div>
                  We were unable to load checkout. Please refresh the page or contact support if the
                  issue persists.
                </div>
              }
              onError={handleError}
            >
              <EmbeddedCheckoutProvider
                stripe={stripePromise}
                options={{
                  fetchClientSecret: wrappedFetchClientSecret,
                  onComplete: () => {
                    Sentry.addBreadcrumb({
                      category: 'checkout',
                      message: 'Stripe checkout completed successfully',
                      level: 'info',
                    });
                  },
                }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </Sentry.ErrorBoundary>
          )}
        </ModalBody>
        <ModalFooter />
      </ModalContent>
    </Modal>
  );
};

export default CheckoutModal;
